import { useState, useEffect } from 'react';
import {
  Camera, Wifi, Server, HardDrive, Database, CheckCircle, XCircle,
  AlertTriangle, ChevronDown, ChevronUp, Clock, Cpu, HardDrive as Disk,
  Activity, Eye, Shield
} from 'lucide-react';
import fetchWithTenant from '../utils/fetchWithTenant';

/**
 * InfrastructureChain - Visual proof chain with expandable evidence
 * Shows Camera -> Network -> NVR -> VMS -> Storage -> Archive
 * Click any node to see the proof/evidence for that link
 *
 * Now fetches real switch port data from the infrastructure API
 * when CCM Monitor is deployed at the site.
 */
const InfrastructureChain = ({ config, latestChecks, correlationResult }) => {
  const [expandedNode, setExpandedNode] = useState(null);
  const [infraData, setInfraData] = useState(null);
  const [infraLoading, setInfraLoading] = useState(false);

  // Fetch real infrastructure chain data (switch port, camera network info)
  useEffect(() => {
    if (!config?.cameraId) return;

    let cancelled = false;
    const fetchInfra = async () => {
      setInfraLoading(true);
      try {
        const res = await fetchWithTenant(
          `/api/recording-assurance/cameras/${encodeURIComponent(config.cameraId)}/infrastructure`
        );
        if (!res.ok) throw new Error('HTTP ' + res.status);
        const data = await res.json();
        if (!cancelled) setInfraData(data);
      } catch (err) {
        console.warn('[InfrastructureChain] Could not load infrastructure data:', err.message);
        if (!cancelled) setInfraData(null);
      } finally {
        if (!cancelled) setInfraLoading(false);
      }
    };
    fetchInfra();
    return () => { cancelled = true; };
  }, [config?.cameraId]);

  if (!config) return null;

  const timeline = latestChecks?.TIMELINE_GAP;
  const thumbnail = latestChecks?.THUMBNAIL_SPOT;
  const health = latestChecks?.VMS_HEALTH;
  const quality = latestChecks?.IMAGE_QUALITY;
  const isMotion = config.recordingMode === 'MOTION' || config.recordingMode === 'SCHEDULED';
  const isIdle = isMotion && config.currentStatus === 'UNKNOWN';

  // Convenience aliases for infrastructure API data
  const infraCamera = infraData?.found ? infraData.camera : null;
  const infraPort = infraData?.found ? infraData.switchPort : null;

  // NVR disk stats from SNMP/WMI probes + agent disk stats
  const nvrDiskStats = infraData?.nvrDiskStats || [];
  const agentDiskStats = infraData?.agentDiskStats || null;

  // VMS storage volumes from health check metadata
  const storageVolumes = health?.metadata?.storage || [];

  // Format bytes to human-readable
  const formatBytes = (bytes) => {
    if (!bytes || bytes === 0) return '0 B';
    const units = ['B', 'KB', 'MB', 'GB', 'TB', 'PB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return (bytes / Math.pow(1024, i)).toFixed(i >= 3 ? 2 : 0) + ' ' + units[i];
  };

  const links = [
    {
      id: 'camera',
      label: 'Camera',
      icon: Camera,
      status: (() => {
        if (isIdle) return 'idle';
        if (quality?.result === 'PASS') return 'ok';
        if (quality?.result === 'FAIL') return 'warning';
        if (thumbnail?.result === 'PASS') return 'ok';
        if (thumbnail?.result === 'FAIL' && isMotion) return 'idle';
        if (thumbnail?.result === 'FAIL') return 'error';
        return 'unknown';
      })(),
      detail: (() => {
        if (infraCamera) {
          const parts = [infraCamera.manufacturer, infraCamera.model].filter(Boolean);
          if (parts.length > 0) return parts.join(' ');
        }
        if (isIdle) return 'No motion detected';
        if (quality?.result === 'PASS') return 'Producing video';
        if (thumbnail?.result === 'PASS') return 'Producing video';
        if (isMotion) return 'Motion camera idle';
        return 'Unable to verify';
      })(),
      evidence: (() => {
        const items = [];

        // Real camera identity from infrastructure API
        if (infraCamera) {
          if (infraCamera.ip) {
            items.push({
              label: 'IP address',
              value: infraCamera.ip,
              status: 'ok'
            });
          }
          if (infraCamera.mac) {
            items.push({
              label: 'MAC address',
              value: infraCamera.mac,
              status: 'ok'
            });
          }
          if (infraCamera.manufacturer || infraCamera.model) {
            items.push({
              label: 'Device',
              value: [infraCamera.manufacturer, infraCamera.model].filter(Boolean).join(' '),
              status: 'ok'
            });
          }
          if (infraCamera.hostname) {
            items.push({
              label: 'Hostname',
              value: infraCamera.hostname,
              status: 'ok'
            });
          }
        }

        if (thumbnail) {
          items.push({
            label: 'Archive thumbnail',
            value: thumbnail.result === 'PASS'
              ? `Retrieved successfully (${thumbnail.metadata?.imageSizeBytes ? Math.round(thumbnail.metadata.imageSizeBytes / 1024) + 'KB' : 'valid image'})`
              : isMotion ? 'Not available (no motion)' : 'Failed to retrieve',
            status: thumbnail.result === 'PASS' ? 'ok' : isMotion ? 'idle' : 'error',
            time: thumbnail.checkedAt
          });
        }
        if (quality) {
          items.push({
            label: 'Image quality',
            value: quality.confidence >= 80
              ? `Score: ${quality.confidence}% -- Sharp, clear image`
              : quality.confidence >= 50
                ? `Score: ${quality.confidence}% -- Minor quality issues`
                : `Score: ${quality.confidence}% -- Quality degraded`,
            status: quality.result === 'PASS' ? 'ok' : 'warning',
            time: quality.checkedAt
          });
          if (quality.metadata?.sharpness) {
            items.push({
              label: 'Sharpness',
              value: `${Math.round(quality.metadata.sharpness)} (${quality.metadata.sharpness >= 300 ? 'sharp' : quality.metadata.sharpness >= 150 ? 'acceptable' : 'blurry'})`,
              status: quality.metadata.sharpness >= 150 ? 'ok' : 'warning'
            });
          }
          if (quality.metadata?.entropy) {
            items.push({
              label: 'Scene complexity',
              value: `${quality.metadata.entropy.toFixed(1)} (${quality.metadata.entropy >= 4.5 ? 'normal scene' : 'possible obstruction'})`,
              status: quality.metadata.entropy >= 4.5 ? 'ok' : 'warning'
            });
          }
          if (quality.metadata?.issues?.length > 0) {
            quality.metadata.issues.forEach(issue => {
              items.push({
                label: 'Issue detected',
                value: issue.type?.replace(/_/g, ' ') + ': ' + (issue.description || '').substring(0, 80),
                status: issue.severity === 'critical' ? 'error' : 'warning'
              });
            });
          }
        }
        if (items.length === 0) {
          items.push({ label: 'Status', value: 'No camera data available yet', status: 'unknown' });
        }
        return items;
      })()
    },
    {
      id: 'network',
      label: 'Network',
      icon: Wifi,
      status: (() => {
        // Real switch port data takes priority
        if (infraPort) {
          if (infraPort.linkStatus === 'UP') return 'ok';
          if (infraPort.linkStatus === 'DOWN') return 'error';
          return 'warning';
        }
        // Fallback to inference
        if (correlationResult?.domains?.network?.events?.length > 0) return 'warning';
        if (timeline?.result === 'PASS' || thumbnail?.result === 'PASS') return 'ok';
        if (isIdle) return 'ok';
        if (timeline?.result === 'FAIL' && health?.result === 'PASS') return 'warning';
        return 'unknown';
      })(),
      detail: (() => {
        if (infraPort) {
          if (infraPort.linkStatus === 'UP') return `${infraPort.switchName} port ${infraPort.portName || infraPort.portId}`;
          return `Port ${infraPort.portName || infraPort.portId} DOWN`;
        }
        if (correlationResult?.domains?.network?.events?.length > 0) return 'Events detected';
        if (timeline?.result === 'PASS' || thumbnail?.result === 'PASS') return 'Connected';
        if (isIdle) return 'Connected (idle)';
        return 'Unable to verify';
      })(),
      evidence: (() => {
        const items = [];

        if (infraLoading) {
          items.push({ label: 'Switch data', value: 'Loading infrastructure data...', status: 'unknown' });
          return items;
        }

        // Real switch port evidence from infrastructure API
        if (infraPort) {
          items.push({
            label: 'Connected switch',
            value: `${infraPort.switchName} (${infraPort.switchManufacturer} ${infraPort.switchModel})`,
            status: 'ok'
          });
          items.push({
            label: 'Switch port',
            value: `Port ${infraPort.portName || infraPort.portId} (ifIndex ${infraPort.portIndex})`,
            status: infraPort.linkStatus === 'UP' ? 'ok' : 'error'
          });
          items.push({
            label: 'Link status',
            value: infraPort.linkStatus === 'UP'
              ? `UP at ${infraPort.speedFormatted}`
              : `DOWN -- camera may be disconnected or powered off`,
            status: infraPort.linkStatus === 'UP' ? 'ok' : 'error'
          });
          if (infraPort.poeEnabled) {
            items.push({
              label: 'PoE power',
              value: infraPort.poeWatts > 0
                ? `${infraPort.poeWatts}W delivered (${infraPort.poeStatus})`
                : `Enabled (${infraPort.poeStatus})`,
              status: infraPort.poeStatus === 'deliveringPower' ? 'ok'
                : infraPort.poeStatus === 'searching' ? 'warning' : 'ok'
            });
          }
          if (infraPort.vlanId) {
            items.push({
              label: 'VLAN',
              value: `VLAN ${infraPort.vlanId}`,
              status: 'ok'
            });
          }
          if (infraPort.adminStatus && infraPort.adminStatus !== 'ENABLED') {
            items.push({
              label: 'Admin status',
              value: `Port is ${infraPort.adminStatus} -- this may prevent connectivity`,
              status: 'error'
            });
          }
        } else if (infraData && !infraData.found) {
          // API responded but no match found
          items.push({
            label: 'Switch mapping',
            value: infraData.reason === 'No MAC table match'
              ? 'Camera MAC not found in switch MAC tables. The camera may be connected through an unmonitored switch or an intermediate device.'
              : infraData.reason === 'No discovered device found'
                ? 'Camera not found in network discovery. Run a network scan to discover this device.'
                : infraData.reason || 'No switch port data available for this camera',
            status: 'unknown'
          });
        }

        // Inference from recording success (shown alongside or as fallback)
        if (!infraPort) {
          if (timeline?.result === 'PASS' || thumbnail?.result === 'PASS') {
            items.push({
              label: 'Connectivity',
              value: 'Camera is successfully transmitting video to the NVR, confirming network path is operational',
              status: 'ok'
            });
          }
        }

        // Correlation data (always show if available)
        if (correlationResult?.domains?.network) {
          const net = correlationResult.domains.network;
          if (net.events?.length > 0) {
            net.events.forEach(evt => {
              items.push({
                label: evt.type === 'PORT_DOWN' ? 'Switch port' : 'Network event',
                value: evt.description,
                status: 'warning',
                time: evt.timestamp
              });
            });
          } else if (!infraPort) {
            items.push({
              label: 'Switch monitoring',
              value: 'No port failures or network errors detected in the correlation window',
              status: 'ok'
            });
          }
        }

        // Monitor alerts
        if (correlationResult?.domains?.monitor) {
          const mon = correlationResult.domains.monitor;
          if (mon.alerts?.length > 0) {
            mon.alerts.slice(0, 3).forEach(alert => {
              items.push({
                label: 'Infrastructure alert',
                value: `${alert.title} -- ${alert.device || 'Unknown device'}`,
                status: alert.severity === 'CRITICAL' ? 'error' : 'warning',
                time: alert.createdAt
              });
            });
          }
        }

        if (items.length === 0) {
          items.push({
            label: 'Network status',
            value: 'Enable CCM Monitor to get detailed switch port and network health data for this camera',
            status: 'unknown'
          });
        }
        return items;
      })()
    },
    {
      id: 'server',
      label: 'NVR Server',
      icon: Server,
      status: (() => {
        if (health?.result === 'PASS') return 'ok';
        if (health?.result === 'FAIL') return 'error';
        return 'unknown';
      })(),
      detail: (() => {
        if (health?.result === 'PASS') {
          const cameras = health.metadata?.cameras;
          return cameras ? cameras.online + '/' + cameras.total + ' online' : 'Healthy';
        }
        if (health?.result === 'FAIL') return 'Issues detected';
        return 'Unable to verify';
      })(),
      evidence: (() => {
        const items = [];
        if (health) {
          items.push({
            label: 'Server health check',
            value: health.result === 'PASS' ? 'VMS server is responding and healthy' : 'Server health check failed',
            status: health.result === 'PASS' ? 'ok' : 'error',
            time: health.checkedAt
          });
          if (health.metadata?.cameras) {
            items.push({
              label: 'Cameras online',
              value: `${health.metadata.cameras.online} of ${health.metadata.cameras.total} cameras reporting to VMS`,
              status: health.metadata.cameras.online === health.metadata.cameras.total ? 'ok' : 'warning'
            });
          }
          if (health.metadata?.healthy !== undefined) {
            items.push({
              label: 'Overall health',
              value: health.metadata.healthy ? 'All server health indicators are normal' : 'One or more health indicators are abnormal',
              status: health.metadata.healthy ? 'ok' : 'warning'
            });
          }
        }
        // Agent data
        if (correlationResult?.domains?.agent) {
          const agent = correlationResult.domains.agent;
          if (agent.agentName) {
            items.push({
              label: 'CCM Agent',
              value: `${agent.agentName} ${agent.agentVersion ? 'v' + agent.agentVersion : ''}`,
              status: 'ok'
            });
          }
          if (agent.lastHeartbeat) {
            items.push({
              label: 'Last heartbeat',
              value: new Date(agent.lastHeartbeat).toLocaleString(),
              status: 'ok',
              time: agent.lastHeartbeat
            });
          }
          if (agent.issues?.length > 0) {
            agent.issues.forEach(issue => {
              items.push({
                label: issue.type === 'HIGH_CPU' ? 'CPU usage' : issue.type === 'DISK_FULL' ? 'Disk usage' : 'Server issue',
                value: issue.description,
                status: issue.type === 'DISK_FULL' ? 'error' : 'warning'
              });
            });
          }
        }
        if (items.length === 0) {
          items.push({ label: 'NVR status', value: 'No server health data available', status: 'unknown' });
        }
        return items;
      })()
    },
    {
      id: 'vms',
      label: 'VMS Software',
      icon: Database,
      status: (() => {
        if (health?.result === 'PASS') return 'ok';
        if (health?.result === 'FAIL') return 'error';
        return 'unknown';
      })(),
      detail: (() => {
        if (health?.result === 'PASS') return 'Running normally';
        if (health?.result === 'FAIL') return 'Service degraded';
        return 'Unable to verify';
      })(),
      evidence: (() => {
        const items = [];
        if (health?.result === 'PASS') {
          items.push({
            label: 'VMS service',
            value: 'VMS API is responding to health queries. Recording service is active.',
            status: 'ok',
            time: health.checkedAt
          });
        }
        items.push({
          label: 'Recording mode',
          value: `Camera configured for ${config.recordingMode} recording`,
          status: 'ok'
        });
        if (config.recordingMode === 'SCHEDULED') {
          items.push({
            label: 'Schedule',
            value: config.currentStatus === 'UNKNOWN'
              ? 'Currently in motion-only window (off-hours)'
              : 'Currently in continuous recording window (active hours)',
            status: config.currentStatus === 'UNKNOWN' ? 'idle' : 'ok'
          });
        }
        return items;
      })()
    },
    {
      id: 'storage',
      label: 'Storage',
      icon: HardDrive,
      status: (() => {
        // Check NVR disk critical levels
        const criticalNvrDisk = nvrDiskStats.some(nvr =>
          (nvr.disks || []).some(d => d.totalBytes > 10*1024*1024*1024 && d.usedPercent > 95)
        );
        const warningNvrDisk = nvrDiskStats.some(nvr =>
          (nvr.disks || []).some(d => d.totalBytes > 10*1024*1024*1024 && d.usedPercent > 85)
        );
        if (criticalNvrDisk) return 'error';
        // Real volume data takes priority
        if (storageVolumes.length > 0) {
          const allOnline = storageVolumes.every(s => s.status === 'Online');
          const anyWriting = storageVolumes.some(s => s.isWriting);
          if (allOnline && anyWriting) return 'ok';
          if (!allOnline) return 'error';
          if (!anyWriting) return 'warning';
        }
        if (timeline?.result === 'PASS') return 'ok';
        if (isIdle) return 'ok';
        if (timeline?.result === 'FAIL' && health?.result === 'PASS') return 'warning';
        if (timeline?.result === 'FAIL') return 'error';
        return 'unknown';
      })(),
      detail: (() => {
        // Show NVR disk usage in summary if available
        const mainNvrDisk = nvrDiskStats.flatMap(n => n.disks || []).find(d => d.totalBytes > 100*1024*1024*1024);
        if (mainNvrDisk) {
          const volSummary = storageVolumes.length > 0 ? storageVolumes.filter(s => s.isWriting).length + ' vol, ' : '';
          return volSummary + mainNvrDisk.usedPercent + '% disk used (' + formatBytes(mainNvrDisk.freeBytes) + ' free)';
        }
        if (storageVolumes.length > 0) {
          const writing = storageVolumes.filter(s => s.isWriting && s.status === 'Online');
          const totalCapacity = writing.reduce((sum, s) => sum + (s.spaceLimitBytes || 0), 0);
          if (totalCapacity > 0) return `${writing.length} vol, ${formatBytes(totalCapacity)}`;
          return `${writing.length} volumes online`;
        }
        if (timeline?.result === 'PASS') {
          const cov = timeline.metadata?.coveragePercent;
          return cov ? cov + '% coverage' : 'Writing footage';
        }
        if (isIdle) return 'Ready (no data)';
        if (timeline?.result === 'FAIL') return 'No footage found';
        return 'Unable to verify';
      })(),
      evidence: (() => {
        const items = [];

        // Real VMS storage volumes
        if (storageVolumes.length > 0) {
          // Group by server for cleaner display
          const serverMap = {};
          storageVolumes.forEach(v => {
            const key = v.serverName || v.serverId || 'Unknown';
            if (!serverMap[key]) serverMap[key] = [];
            serverMap[key].push(v);
          });
          Object.entries(serverMap).forEach(([serverName, vols]) => {
            const activeVols = vols.filter(v => v.isWriting && !v.isBackup);
            const totalCapacity = activeVols.reduce((sum, v) => sum + (v.spaceLimitBytes || 0), 0);
            const allOnline = vols.every(v => v.status === 'Online');
            items.push({
              label: serverName.replace(/^Server\s+/i, ''),
              value: allOnline
                ? `${activeVols.length} active volume${activeVols.length !== 1 ? 's' : ''}, ${formatBytes(totalCapacity)} capacity, all online`
                : `${vols.filter(v => v.status !== 'Online').length} volume(s) offline`,
              status: allOnline ? 'ok' : 'error'
            });
            vols.forEach(vol => {
              items.push({
                label: vol.isBackup ? 'Backup volume' : 'Recording volume',
                value: `${vol.path} -- ${vol.status}${vol.isWriting ? ', writing' : ', idle'}${vol.spaceLimitBytes ? ', ' + formatBytes(vol.spaceLimitBytes) : ''}${vol.dbReady ? '' : ' (DB not ready)'}`,
                status: vol.status === 'Online' && vol.isWriting ? 'ok'
                  : vol.status === 'Online' ? 'idle'
                  : 'error'
              });
            });
          });
        }

        // NVR OS-level disk usage (from SNMP/WMI probe)
        if (nvrDiskStats.length > 0) {
          nvrDiskStats.forEach(nvr => {
            const significantDisks = (nvr.disks || []).filter(d => d.totalBytes > 10 * 1024 * 1024 * 1024);
            if (significantDisks.length > 0) {
              items.push({
                label: nvr.hostname || nvr.ipAddress,
                value: 'OS-level disk usage (SNMP)',
                status: 'ok'
              });
              significantDisks.forEach(disk => {
                const pct = disk.usedPercent || 0;
                const diskStatus = pct > 95 ? 'error' : pct > 85 ? 'warning' : 'ok';
                items.push({
                  label: disk.path,
                  value: formatBytes(disk.usedBytes) + ' / ' + formatBytes(disk.totalBytes) + ' (' + pct + '% used), ' + formatBytes(disk.freeBytes) + ' free',
                  status: diskStatus
                });
              });
            }
          });
        }

        // Agent host disk stats (agent's own machine)
        if (agentDiskStats && agentDiskStats.disks && agentDiskStats.disks.length > 0) {
          const significantDisks = agentDiskStats.disks.filter(d => d.totalBytes > 10 * 1024 * 1024 * 1024);
          if (significantDisks.length > 0) {
            items.push({
              label: agentDiskStats.agentHostname || 'Agent',
              value: 'Agent host disk (v' + (agentDiskStats.agentVersion || '?') + ')',
              status: 'ok'
            });
            significantDisks.forEach(disk => {
              const pct = disk.usedPercent || 0;
              const diskStatus = pct > 95 ? 'error' : pct > 85 ? 'warning' : 'ok';
              items.push({
                label: disk.path,
                value: formatBytes(disk.usedBytes || (disk.totalBytes - disk.freeBytes)) + ' / ' + formatBytes(disk.totalBytes) + ' (' + pct + '% used)',
                status: diskStatus
              });
            });
          }
        }

                if (timeline) {
          items.push({
            label: 'Timeline analysis',
            value: timeline.result === 'PASS'
              ? `${timeline.metadata?.coveragePercent || 0}% recording coverage in the last check window`
              : isMotion
                ? 'No recording in check window (motion camera, no activity)'
                : 'No recorded footage found in the check window',
            status: timeline.result === 'PASS' ? 'ok' : isMotion ? 'idle' : 'error',
            time: timeline.checkedAt
          });
          if (timeline.metadata?.periodCount !== undefined) {
            items.push({
              label: 'Recording segments',
              value: `${timeline.metadata.periodCount} segment(s) found in archive`,
              status: timeline.metadata.periodCount > 0 ? 'ok' : isMotion ? 'idle' : 'warning'
            });
          }
          if (timeline.metadata?.gapCount > 0) {
            items.push({
              label: 'Gaps detected',
              value: `${timeline.metadata.gapCount} gap(s) totaling ${Math.round((timeline.metadata.totalGapMs || 0) / 60000)} minutes`,
              status: 'warning'
            });
          }
          if (timeline.metadata?.baseCoverage !== undefined && timeline.metadata?.gapPenalty > 0) {
            items.push({
              label: 'Score breakdown',
              value: `Base coverage: ${timeline.metadata.baseCoverage}%, gap penalty: -${timeline.metadata.gapPenalty}%`,
              status: 'ok'
            });
          }
        }
        if (items.length === 0) {
          items.push({ label: 'Storage status', value: 'No timeline data available', status: 'unknown' });
        }
        return items;
      })()
    },
    {
      id: 'archive',
      label: 'Archive',
      icon: CheckCircle,
      status: (() => {
        if (config.currentStatus === 'VERIFIED') return 'ok';
        if (isIdle) return 'idle';
        if (config.currentStatus === 'DEGRADED') return 'warning';
        if (config.currentStatus === 'FAILED') return 'error';
        return 'unknown';
      })(),
      detail: (() => {
        if (config.currentStatus === 'VERIFIED') return 'Footage verified';
        if (isIdle) return 'No motion to archive';
        if (config.currentStatus === 'DEGRADED') return 'Partial gaps';
        if (config.currentStatus === 'FAILED') return 'Not verified';
        return 'Checking...';
      })(),
      evidence: (() => {
        const items = [];
        items.push({
          label: 'Overall status',
          value: config.currentStatus === 'VERIFIED'
            ? `Recording Assurance confirms footage is being written and is retrievable. Confidence: ${config.currentConfidence}%`
            : isIdle
              ? 'Camera is in idle mode. All infrastructure is operational and ready to record when motion is detected.'
              : `Current status: ${config.currentStatus}. Confidence: ${config.currentConfidence}%`,
          status: config.currentStatus === 'VERIFIED' ? 'ok' : isIdle ? 'idle' : 'warning'
        });
        items.push({
          label: 'Last verified',
          value: config.lastCheckedAt ? new Date(config.lastCheckedAt).toLocaleString() : 'Not yet checked',
          status: 'ok',
          time: config.lastCheckedAt
        });
        items.push({
          label: 'Verification layers',
          value: `5 independent checks: VMS health, timeline analysis, thumbnail proof, image quality, baseline comparison`,
          status: 'ok'
        });
        return items;
      })()
    }
  ];

  const statusColors = {
    ok: '#22c55e', warning: '#f59e0b', error: '#ef4444', idle: '#0ea5e9', unknown: '#64748b'
  };
  const statusBg = {
    ok: 'rgba(34, 197, 94, 0.12)', warning: 'rgba(245, 158, 11, 0.12)',
    error: 'rgba(239, 68, 68, 0.12)', idle: 'rgba(14, 165, 233, 0.12)',
    unknown: 'rgba(100, 116, 139, 0.12)'
  };

  const hasError = links.some(l => l.status === 'error');
  const hasWarning = links.some(l => l.status === 'warning');
  const allOk = links.every(l => l.status === 'ok' || l.status === 'idle');

  return (
    <div className="ra-card" style={{ marginBottom: '20px' }}>
      <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--border-color)', display: 'flex', alignItems: 'center', gap: '10px' }}>
        <Shield size={18} style={{ color: '#06b6d4' }} />
        <h3 style={{ margin: 0, fontSize: '15px', color: 'var(--text-primary)' }}>Infrastructure chain</h3>
        <span style={{ fontSize: '11px', color: 'var(--text-muted)', fontStyle: 'italic' }}>Click any link to see proof</span>
        <span style={{
          marginLeft: 'auto', fontSize: '12px', fontWeight: 600, padding: '2px 10px', borderRadius: '4px',
          background: allOk ? 'rgba(34,197,94,0.12)' : hasError ? 'rgba(239,68,68,0.12)' : 'rgba(245,158,11,0.12)',
          color: allOk ? '#22c55e' : hasError ? '#ef4444' : '#f59e0b'
        }}>
          {allOk ? 'All systems operational' : hasError ? 'Issue detected' : 'Warning'}
        </span>
      </div>

      {/* Chain nodes */}
      <div className="ra-infra-chain-scroll" style={{ padding: '20px', overflowX: 'auto' }}>
        <div className="ra-infra-chain-nodes" style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'center', gap: '0', minWidth: '600px' }}>
          {links.map((link, i) => {
            const Icon = link.icon;
            const color = statusColors[link.status];
            const bg = statusBg[link.status];
            const isExpanded = expandedNode === link.id;
            return (
              <div key={link.id} className="ra-infra-chain-node" style={{ display: 'flex', alignItems: 'flex-start' }}>
                <div
                  style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '90px', cursor: 'pointer' }}
                  onClick={() => setExpandedNode(isExpanded ? null : link.id)}
                >
                  <div style={{
                    width: '48px', height: '48px', borderRadius: '12px',
                    background: bg, border: '2px solid ' + color,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    position: 'relative', transition: 'transform 0.15s',
                    transform: isExpanded ? 'scale(1.1)' : 'scale(1)',
                    boxShadow: isExpanded ? '0 0 12px ' + color + '40' : 'none'
                  }}>
                    <Icon size={22} style={{ color }} />
                    <div style={{
                      position: 'absolute', bottom: '-3px', right: '-3px',
                      width: '14px', height: '14px', borderRadius: '50%',
                      background: color, border: '2px solid var(--bg-secondary)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center'
                    }}>
                      {link.status === 'ok' && <span style={{ color: 'var(--text-primary)', fontSize: '8px', fontWeight: 700 }}>&#10003;</span>}
                      {link.status === 'error' && <span style={{ color: 'var(--text-primary)', fontSize: '8px', fontWeight: 700 }}>&#10005;</span>}
                      {link.status === 'warning' && <span style={{ color: 'var(--text-primary)', fontSize: '8px', fontWeight: 700 }}>!</span>}
                      {link.status === 'idle' && <span style={{ color: 'var(--text-primary)', fontSize: '8px', fontWeight: 700 }}>~</span>}
                    </div>
                  </div>
                  <div style={{ marginTop: '8px', textAlign: 'center', minHeight: '36px' }}>
                    <div style={{ fontSize: '11px', fontWeight: 600, color }}>{link.label}</div>
                    <div style={{ fontSize: '9px', color: 'var(--text-muted)', maxWidth: '90px' }}>{link.detail}</div>
                  </div>
                  {/* Expand indicator */}
                  <div style={{ marginTop: '2px' }}>
                    {isExpanded
                      ? <ChevronUp size={12} style={{ color: 'var(--text-muted)' }} />
                      : <ChevronDown size={12} style={{ color: 'var(--text-muted)', opacity: 0.4 }} />}
                  </div>
                </div>

                {/* Connector */}
                {i < links.length - 1 && (
                  <div className="ra-infra-chain-connector" style={{
                    width: '32px', height: '2px', margin: '0 2px', marginTop: '24px',
                    background: (() => {
                      const nextStatus = links[i + 1].status;
                      if (nextStatus === 'error') return '#ef4444';
                      if (nextStatus === 'warning') return '#f59e0b';
                      if (link.status === 'error') return '#ef4444';
                      return 'rgba(255,255,255,0.15)';
                    })(),
                    position: 'relative'
                  }}>
                    <div style={{
                      position: 'absolute', right: '-4px', top: '-3px',
                      width: 0, height: 0,
                      borderTop: '4px solid transparent', borderBottom: '4px solid transparent',
                      borderLeft: '6px solid ' + (links[i + 1].status === 'error' ? '#ef4444' : links[i + 1].status === 'warning' ? '#f59e0b' : 'rgba(255,255,255,0.15)')
                    }} />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Evidence Panel */}
      {expandedNode && (() => {
        const link = links.find(l => l.id === expandedNode);
        if (!link) return null;
        const color = statusColors[link.status];
        return (
          <div style={{
            margin: '0 20px 16px', padding: '16px',
            background: 'var(--bg-primary)', borderRadius: '10px',
            border: '1px solid ' + color + '30'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
              <link.icon size={16} style={{ color }} />
              <span style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-primary)' }}>{link.label} evidence</span>
              <span style={{ fontSize: '11px', color: 'var(--text-muted)', marginLeft: 'auto' }}>
                {link.evidence.filter(e => e.status === 'ok').length}/{link.evidence.length} checks passed
              </span>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {link.evidence.map((item, i) => (
                <div key={i} style={{
                  display: 'flex', alignItems: 'flex-start', gap: '10px', padding: '8px 12px',
                  background: 'var(--bg-secondary)', borderRadius: '8px',
                  borderLeft: '3px solid ' + statusColors[item.status]
                }}>
                  <div style={{
                    width: '8px', height: '8px', borderRadius: '50%', marginTop: '5px',
                    background: statusColors[item.status], flexShrink: 0
                  }} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '11px', fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.3px' }}>
                      {item.label}
                    </div>
                    <div style={{ fontSize: '12px', color: 'var(--text-secondary)', lineHeight: 1.5, marginTop: '2px' }}>
                      {item.value}
                    </div>
                    {item.time && (
                      <div style={{ fontSize: '10px', color: 'var(--text-muted)', marginTop: '2px' }}>
                        Checked: {new Date(item.time).toLocaleString()}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })()}

      {/* Summary */}
      <div style={{ padding: '0 20px 16px' }}>
        <p style={{ fontSize: '12px', color: 'var(--text-muted)', lineHeight: 1.6, margin: 0, textAlign: 'center' }}>
          {allOk && !links.some(l => l.status === 'idle') &&
            'Every link in the chain from camera to archive is operational. Footage is being captured, transmitted, recorded, and verified.'}
          {allOk && links.some(l => l.status === 'idle') &&
            'All infrastructure is operational. The camera is in motion-only mode with no current activity. When motion is detected, the full chain will activate automatically.'}
          {hasError && !hasWarning &&
            'A break has been detected in the recording chain. Click the highlighted link to see what went wrong.'}
          {hasWarning && !hasError &&
            'The recording chain has a weak link. Click the highlighted link for details and recommended action.'}
          {hasError && hasWarning &&
            'Multiple issues detected in the recording chain. Click each highlighted link to review the evidence.'}
        </p>
      </div>
    </div>
  );
};

export default InfrastructureChain;
